import { Link } from "@tanstack/react-router";
import { forwardRef, memo } from "react";

import Icon from "@/components/Icon/Icon";
import HStack from "@/components/Layout/HStack";
import { cn } from "@/lib/cn";

import type { TabWithIndicatorProps } from "./types";

/**
 * タブコンポーネント内部のインジケーター
 *
 * アクティブ状態を視覚的に示すための下線インジケーター
 */
const TabIndicator = memo(
  ({ active, disabled }: { active?: boolean; disabled?: boolean }) => (
    <span
      aria-hidden="true"
      className={cn(
        "absolute bottom-0 left-0 h-1 w-full translate-y-1/2 rounded-full bg-primary transition-opacity duration-150",
        active ? "opacity-100" : "opacity-0",
        "group-hover:opacity-70",
        disabled && "bg-neutral-muted-on-fill",
      )}
    />
  ),
);

/**
 * タブアイコン（外部リンク用）
 */
const TabExternalIcon = memo(() => (
  <Icon iconName="SquareArrowUpRightIcon" size={16} />
));

/**
 * ナビゲーションタブのコンポーネント
 * TanStack RouterのLinkと統合されたタブUIを提供します
 *
 * タブの表示とインタラクションのみを担当。
 * /components/ui/tabs.tsx(Radix UI) ではなく独自実装を選択した理由：
 *
 * 1. スクロール機能: Radix UI の Tabs にはネイティブなスクロール機能がない
 * 2. TanStack Router との統合: ルーティングと密接に連携する必要があり、Radix ではこの統合が難しい
 * 3. 動的なアクティブ状態: URL のパス、クエリパラメータ、ハッシュに基づく複雑なアクティブ状態判定が必要
 * 4. カスタムデザイン要件: アニメーション、インジケーター、外部リンクアイコンなど細かい UI 要件がある
 * 5. パフォーマンス最適化: スクロール処理やタブの動的な状態変更に対して特別な最適化が必要
 *
 */
export const Tab = memo(
  forwardRef<HTMLAnchorElement, TabWithIndicatorProps>(function Tab(
    { children, disabled, target, active, to, className, ...props },
    ref,
  ) {
    return (
      <Link
        ref={ref}
        to={to}
        role="tab"
        aria-disabled={disabled}
        aria-selected={active}
        target={target}
        data-state={active ? "active" : "inactive"}
        className={cn(
          "group relative whitespace-nowrap px-4 py-2 outline-hidden",
          "font-medium text-muted-foreground hover:text-foreground focus-visible:text-foreground",
          "transition-colors duration-150",
          active && "font-semibold text-foreground",
          disabled && "text-neutral-muted-on-fill pointer-events-none",
          className,
        )}
        {...props}
      >
        <HStack className="items-center gap-2">
          {children}
          {target && <TabExternalIcon />}
        </HStack>
        <TabIndicator active={active} disabled={disabled} />
      </Link>
    );
  }),
);
